/*
 * @forgerock/ping-javascript-sdk
 *
 * index.ts
 *
 * Copyright (c) 2020 - 2025 Ping Identity Corporation. All rights reserved.
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { callbackType } from '@forgerock/sdk-types';
import { parseDeviceNameText, parseDisplayRecoveryCodesText } from './script-parser.js';
/**
 * Utility for handling recovery code nodes.
 *
 * Example:
 *
 * ```js
 * // Determine if step is Display Recovery Codes step
 * const isDisplayRecoveryCodesStep = RecoveryCodes.isDisplayStep(step);
 * if (isDisplayRecoveryCodesStep) {
 *   const recoveryCodes = RecoveryCodes.getCodes(step);
 *   // Do the UI needful
 * }
 * ```
 */
export class RecoveryCodes {
    static getDeviceName(step) {
        const text = this.getDisplayCallback(step)?.getOutputByName('message', '') ?? '';
        return parseDeviceNameText(text);
    }
    /**
     * Retrieves the recovery codes by parsing the JavaScript message text in callback.
     *
     * @param step The step to evaluate
     * @return Recovery Code values in array
     */
    static getCodes(step) {
        const text = this.getDisplayCallback(step)?.getOutputByName('message', '');
        return parseDisplayRecoveryCodesText(text || '');
    }
    /**
     * Determines if the given step is a Display Recovery Codes step.
     *
     * @param step The step to evaluate
     * @return Is this step a Display Recovery Codes step
     */
    static isDisplayStep(step) {
        return !!this.getDisplayCallback(step);
    }
    /**
     * Gets the recovery codes step.
     *
     * @param step The step to evaluate
     * @return gets the Display Recovery Codes' callback
     */
    static getDisplayCallback(step) {
        return step
            .getCallbacksOfType(callbackType.TextOutputCallback)
            .find((x) => {
            const cb = x.getOutputByName('message', undefined);
            return cb && (cb.includes('Recovery Codes') || cb.includes('recovery codes'));
        });
    }
}
